// src/pages/DashboardScreen.jsx
// Dashboard premium operacional.
// Central do consultório: agenda, financeiro, leads, pendências e visão executiva.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { fetchDashboard, fetchDashboardInsight } from "../services/dashboardServices";

import AgentFloatingChat from "../components/AgentFloatingChat";
import FounderBadge from "../components/FounderBadge";

import "../sass/pages/dashboard.scss";

import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CalendarDays,
  CalendarPlus,
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  LayoutDashboard,
  Menu,
  RefreshCw,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  WalletCards,
} from "lucide-react";

import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const APP_ROUTES = {
  CLIENTES: "/clients",
  CLIENTES_NOVO: "/clients/new",
  AGENDA: "/schedule",
  AGENDAMENTO_NOVO: "/schedule/new",
  FINANCEIRO: "/finance",
  LEADS: "/leads",
  LEAD_NOVO: "/leads/new",
  RELATORIOS: "/reports",
  SERVICOS: "/settings",
  SESSAO: "/session-control",
  SESSAO_NOVA: "/sessions/new",
  PERFIL: "/profile",
};

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  RECURSOS: "recursos",
};

const DEFAULT_RANGE = "7d";
const DASH_LAYOUT_KEY = "psy_dashboard_card_order_v1";
const DEFAULT_CARD_ORDER = ["agenda", "receita", "pendente", "leads"];
const DASH_SECTION_LAYOUT_KEY = "psy_dashboard_section_order_v2";
const DEFAULT_SECTION_ORDER = [
  "hero",
  "today",
  "attention",
  "agent-summary",
  "commands",
  "kpis",
  "analytics",
  "filters",
  "onboarding",
];

const SECTION_LABELS = {
  hero: "Resumo operacional do dia",
  onboarding: "Primeiros passos",
  filters: "Período e filtros",
  today: "Sua rotina de hoje",
  commands: "Ações rápidas",
  kpis: "Indicadores principais",
  "agent-summary": "Plano inteligente do dia",
  attention: "Próximas ações",
  analytics: "Gráficos e análises",
};

const COLORS = {
  blue: "#4f8cff",
  cyan: "#22d3ee",
  green: "#22c55e",
  yellow: "#f59e0b",
  red: "#ef4444",
  violet: "#a855f7",
  slate: "#94a3b8",
  white: "#ffffff",
};

const STATUS_LABEL = {
  pendente: "Pendente",
  confirmado: "Confirmado",
  aguardando_confirmacao: "Aguardando",
  baixado: "Encerrado",
  cancelado: "Cancelado",
  expirado: "Expirado",
  no_show: "No-show",
};

const STATUS_COLORS = {
  pendente: COLORS.yellow,
  confirmado: COLORS.blue,
  aguardando_confirmacao: COLORS.cyan,
  baixado: COLORS.green,
  cancelado: COLORS.red,
  expirado: COLORS.slate,
  no_show: "#fb7185",
};

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const EMPTY_WEEKDAY = [0, 0, 0, 0, 0, 0, 0];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";



const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const GOOGLE_STATUS_ENDPOINT =
  `${API_BASE_URL}/psy/integracoes/google/status`;

const USER_ME_ENDPOINT =
  `${API_BASE_URL}/psy/user/me`;

const FRASE_DASHBOARD_ENDPOINT =
  `${API_BASE_URL}/psy/frases-dashboard/aleatoria`;

const FRASE_DASHBOARD_STORAGE_PREFIX =
  "psyassist:frase-dashboard";

async function fetchGoogleIntegrationStatus(token) {
  if (!token) {
    return { conectado: false };
  }

  try {
    const response = await fetch(GOOGLE_STATUS_ENDPOINT, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      return { conectado: false };
    }

    const data = await response.json();

    return {
      ...data,
      conectado: Boolean(data?.conectado),
    };
  } catch {
    return { conectado: false };
  }
}

async function fetchCurrentUserProfile(token) {
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(USER_ME_ENDPOINT, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    const refreshedToken = response.headers.get("x-new-token");

    if (refreshedToken) {
      localStorage.setItem(AUTH_KEYS.TOKEN, refreshedToken);
      sessionStorage.setItem(AUTH_KEYS.TOKEN, refreshedToken);
    }

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn(
      "Não foi possível consultar o perfil atual para verificar o Pix:",
      error
    );

    return null;
  }
}

function hasConfiguredPix(user = {}) {
  const pix = user?.pix || {};

  return Boolean(
    String(pix?.tipo || "").trim() &&
    String(pix?.chave || "").trim() &&
    String(pix?.favorecido || "").trim()
  );
}

function isPremiumUser(user = {}) {
  if (user?.premiumLiberado === true) {
    return true;
  }

  if (
    user?.premium === true &&
    isStatusLiberado(user?.statusAssinatura) &&
    user?.assinaturaAtiva !== false
  ) {
    return true;
  }

  return (
    isPremiumByPlan(user?.plano) &&
    isStatusLiberado(user?.statusAssinatura) &&
    user?.assinaturaAtiva !== false
  );
}
function getJwtIssuedAt(token = "") {
  try {
    const parts = String(token || "").split(".");

    if (parts.length < 2) {
      return "";
    }

    const normalizedPayload = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const paddedPayload =
      normalizedPayload +
      "=".repeat((4 - (normalizedPayload.length % 4)) % 4);

    const payload = JSON.parse(atob(paddedPayload));

    return String(payload?.iat || "");
  } catch {
    return "";
  }
}

function getFraseDashboardStorageKey() {
  const userId = readAuth(AUTH_KEYS.USER_ID) || "usuario";
  const token = readAuth(AUTH_KEYS.TOKEN);
  const issuedAt = getJwtIssuedAt(token);

  /*
   * O iat do JWT faz a frase pertencer ao login atual.
   * Assim, um novo login gera uma nova chave e recebe outra frase
   * sem exigir mudança na tela de Login.
   */
  return `${FRASE_DASHBOARD_STORAGE_PREFIX}:${userId}:${issuedAt || "sessao"}`;
}

function readCachedFraseDashboard() {
  try {
    const key = getFraseDashboardStorageKey();
    const raw = sessionStorage.getItem(key);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    return parsed?.texto ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedFraseDashboard(frase) {
  if (!frase?.texto) {
    return;
  }

  try {
    const currentKey = getFraseDashboardStorageKey();

    Object.keys(sessionStorage)
      .filter(
        (key) =>
          key.startsWith(`${FRASE_DASHBOARD_STORAGE_PREFIX}:`) &&
          key !== currentKey
      )
      .forEach((key) => sessionStorage.removeItem(key));

    sessionStorage.setItem(
      currentKey,
      JSON.stringify(frase)
    );
  } catch {
    // O cache é opcional. A frase continua funcionando sem ele.
  }
}

async function fetchFraseDashboard(token) {
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(FRASE_DASHBOARD_ENDPOINT, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    const refreshedToken = response.headers.get("x-new-token");

    if (refreshedToken) {
      localStorage.setItem(AUTH_KEYS.TOKEN, refreshedToken);
      sessionStorage.setItem(AUTH_KEYS.TOKEN, refreshedToken);
    }

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const frase = data?.frase;

    if (!frase?.texto) {
      return null;
    }

    return {
      id: frase.id || null,
      texto: String(frase.texto || "").trim(),
      autor: String(frase.autor || "").trim(),
      obra: String(frase.obra || "").trim(),
      ano: frase.ano ?? null,
      categoria: frase.categoria || "outro",
    };
  } catch (error) {
    console.warn(
      "Não foi possível carregar o pensamento do dia:",
      error
    );

    return null;
  }
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();
  return clean ? clean.split(/\s+/)[0] : "";
}

/*
 * Saudação baseada no horário oficial usado pelo dashboard.
 *
 * Regras:
 * - 05:00 até 11:59: Bom dia
 * - 12:00 até 17:59: Boa tarde
 * - 18:00 até 04:59: Boa noite
 */
function getGreeting(timeZone = "America/Sao_Paulo", date = new Date()) {
  try {
    const hourText = new Intl.DateTimeFormat("pt-BR", {
      timeZone,
      hour: "2-digit",
      hourCycle: "h23",
    }).format(date);

    const hour = Number(hourText);

    if (Number.isFinite(hour)) {
      if (hour >= 5 && hour < 12) {
        return "Bom dia";
      }

      if (hour >= 12 && hour < 18) {
        return "Boa tarde";
      }

      return "Boa noite";
    }
  } catch (error) {
    console.warn("Não foi possível calcular a saudação pelo fuso configurado:", error);
  }

  const localHour = date.getHours();

  if (localHour >= 5 && localHour < 12) {
    return "Bom dia";
  }

  if (localHour >= 12 && localHour < 18) {
    return "Boa tarde";
  }

  return "Boa noite";
}

function getNumber(value, fallback = 0) {
  const number = Number(value ?? fallback);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeArray(value, fallback = []) {
  return Array.isArray(value) ? value : fallback;
}

function formatDateBR(dateLike) {
  if (!dateLike) return "";

  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatShortDateBR(dateLike) {
  if (!dateLike) return "";

  const date = new Date(`${String(dateLike).slice(0, 10)}T00:00:00`);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}

function formatTimeBR(dateLike) {
  if (!dateLike) return "--:--";

  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatLastUpdated(dateLike) {
  if (!dateLike) return "";

  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getErrorMessage(error) {
  const raw =
    error?.response?.data?.message ||
    error?.message ||
    String(error || "Erro desconhecido.");

  if (raw.includes("Token/ID ausente")) {
    return "Sessão expirada. Faça login novamente.";
  }

  return raw;
}

function normalizeText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function parseJsonStorage(value, fallback = {}) {
  try {
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function isPremiumByPlan(plano) {
  const value = normalizeText(plano);
  return value === "premium" || value === "pro" || value === "psyassist premium" || value.includes("premium");
}

function isStatusLiberado(statusAssinatura) {
  const status = normalizeText(statusAssinatura || "active");
  return ["active", "trialing", "past_due"].includes(status);
}

function isAssinaturaAtiva(value) {
  if (value === "" || value === null || value === undefined) return true;
  return String(value).trim().toLowerCase() !== "false";
}

function getStoredRecursos() {
  return parseJsonStorage(readAuth(AUTH_KEYS.RECURSOS), {});
}

function canUseAnyPremiumResource() {
  const recursos = getStoredRecursos();

  if (
    recursos?.ia === true ||
    recursos?.insightsIa === true ||
    recursos?.cobrancas === true ||
    recursos?.relatoriosAvancados === true ||
    recursos?.googleAgenda === true
  ) {
    return true;
  }

  return (
    isPremiumByPlan(readAuth(AUTH_KEYS.PLAN)) &&
    isStatusLiberado(readAuth(AUTH_KEYS.STATUS_ASSINATURA)) &&
    isAssinaturaAtiva(readAuth(AUTH_KEYS.ASSINATURA_ATIVA))
  );
}

function getPlanoDisplay() {
  const plano = readAuth(AUTH_KEYS.PLAN) || "Essencial";
  const status = normalizeText(readAuth(AUTH_KEYS.STATUS_ASSINATURA));
  const premium = canUseAnyPremiumResource();

  if (status === "past_due") return `${plano} • pendente`;
  if (premium) return plano || "Premium";
  return plano || "Essencial";
}

function getDailyEvolutionSource(dash) {
  return (
    dash?.charts?.dailyEvolution ||
    dash?.charts?.weeklyEvolution ||
    dash?.chartData?.dailyEvolution ||
    dash?.chartData?.weeklyEvolution ||
    dash?.dailyEvolution ||
    dash?.weeklyEvolution ||
    []
  );
}

function getRevenueDailySource(dash) {
  return dash?.charts?.revenueDaily || dash?.financial?.revenueDaily || [];
}

function getTypeDistributionSource(dash) {
  return (
    dash?.charts?.typeDistribution ||
    dash?.chartData?.typeDistribution ||
    dash?.typeDistribution ||
    []
  );
}

function getWeekdaySource(dash) {
  return dash?.charts?.byWeekday || dash?.chartData?.byWeekday || dash?.byWeekday || EMPTY_WEEKDAY;
}

function getAppointmentClientName(appointment) {
  return (
    appointment?.cliente?.nome ||
    appointment?.clienteId?.nome ||
    appointment?.nomeCliente ||
    "Cliente"
  );
}

function getAppointmentService(appointment) {
  return appointment?.servico || appointment?.tipoSessao || "Atendimento";
}

function getAppointmentStatusLabel(status = "") {
  const key = String(status || "pendente").trim();
  return STATUS_LABEL[key] || key || "Pendente";
}

function getAppointmentStatusTone(status = "") {
  const key = String(status || "").trim().toLowerCase();

  if (["confirmado", "baixado"].includes(key)) return "green";
  if (["pendente", "aguardando_confirmacao"].includes(key)) return "warning";
  if (["cancelado", "expirado", "no_show"].includes(key)) return "danger";

  return "blue";
}

/*
 * Retorna true somente quando a confirmação veio do próprio cliente
 * através do link público enviado no lembrete.
 */
function wasConfirmedByClient(appointment = {}) {
  return (
    appointment?.confirmadoPeloCliente === true ||
    appointment?.confirmadoPorLink === true
  );
}

function getClientConfirmationLabel(appointment = {}) {
  return wasConfirmedByClient(appointment)
    ? "Cliente confirmou"
    : "";
}

function isAppointmentActiveToday(appointment = {}) {
  const status = String(appointment?.status || "").trim().toLowerCase();
  return !["cancelado", "baixado", "expirado", "no_show"].includes(status);
}

function getStatusData(dash) {
  const source =
    dash?.charts?.statusDistribution ||
    Object.entries(dash?.kpis?.appointmentsByStatus || {}).map(([status, total]) => ({
      status,
      label: STATUS_LABEL[status] || status,
      total,
    }));

  return normalizeArray(source)
    .map((item) => ({
      status: item.status || item._id || item.label,
      label: item.label || STATUS_LABEL[item.status] || item.status || "Status",
      total: getNumber(item.total ?? item.value),
    }))
    .filter((item) => item.total > 0);
}

function getLeadOriginData(dash) {
  return normalizeArray(dash?.charts?.leadOrigins || dash?.leads?.origins).map(
    (item) => ({
      origem: item.origem || item.name || "Origem",
      total: getNumber(item.total ?? item.value),
    })
  );
}

function CustomTooltip({ active, payload, label, moneyFormatter }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="dash-tooltip">
      <strong>{label}</strong>
      {payload.map((item, index) => {
        const value = getNumber(item.value);
        const formatted =
          item.dataKey === "revenue" || item.name === "Receita"
            ? moneyFormatter.format(value)
            : value;

        return (
          <span key={`${item.name}-${index}`}>
            {item.name || item.dataKey}: <b>{formatted}</b>
          </span>
        );
      })}
    </div>
  );
}

function ChartFrame({ height = 290, children }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const nextWidth = Math.floor(rect.width || 0);

      setWidth((current) => {
        if (Math.abs(current - nextWidth) < 2) return current;
        return nextWidth;
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="dash-chart-frame"
      style={{ minHeight: height }}
    >
      {width > 24 ? children(Math.max(width, 260), height) : null}
    </div>
  );
}

export default function DashboardScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dash, setDash] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DASH_LAYOUT_KEY) || "[]");
      return Array.isArray(saved) && saved.length === DEFAULT_CARD_ORDER.length
        ? saved
        : DEFAULT_CARD_ORDER;
    } catch {
      return DEFAULT_CARD_ORDER;
    }
  });
  const [draggedCard, setDraggedCard] = useState("");
  const [sectionOrder, setSectionOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DASH_SECTION_LAYOUT_KEY) || "[]");
      const valid = Array.isArray(saved) &&
        saved.length === DEFAULT_SECTION_ORDER.length &&
        DEFAULT_SECTION_ORDER.every((id) => saved.includes(id));
      return valid ? saved : DEFAULT_SECTION_ORDER;
    } catch {
      return DEFAULT_SECTION_ORDER;
    }
  });
  const [draggedSection, setDraggedSection] = useState("");
  const [customizingLayout, setCustomizingLayout] = useState(false);
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    carregando: true,
    conectado: false,
  });

  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [currentUserProfileLoaded, setCurrentUserProfileLoaded] = useState(false);

  const [fraseDashboard, setFraseDashboard] = useState(
    () => readCachedFraseDashboard()
  );
  const [fraseDashboardLoading, setFraseDashboardLoading] = useState(
    () => !readCachedFraseDashboard()
  );

  const nome = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  useEffect(() => {
    let ativo = true;

    async function carregarGoogleStatus() {
      const token = readAuth(AUTH_KEYS.TOKEN);
      const status = await fetchGoogleIntegrationStatus(token);

      if (ativo) {
        setGoogleCalendarStatus({
          carregando: false,
          conectado: Boolean(status?.conectado),
        });
      }
    }

    carregarGoogleStatus();

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregarPerfilAtual() {
      const token = readAuth(AUTH_KEYS.TOKEN);
      const profile = await fetchCurrentUserProfile(token);

      if (!ativo) {
        return;
      }

      setCurrentUserProfile(profile);
      setCurrentUserProfileLoaded(true);
    }

    carregarPerfilAtual();

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregarFraseDashboard() {
      const cached = readCachedFraseDashboard();

      if (cached?.texto) {
        if (ativo) {
          setFraseDashboard(cached);
          setFraseDashboardLoading(false);
        }

        return;
      }

      const token = readAuth(AUTH_KEYS.TOKEN);

      if (!token) {
        if (ativo) {
          setFraseDashboard(null);
          setFraseDashboardLoading(false);
        }

        return;
      }

      if (ativo) {
        setFraseDashboardLoading(true);
      }

      const frase = await fetchFraseDashboard(token);

      if (!ativo) {
        return;
      }

      if (frase?.texto) {
        writeCachedFraseDashboard(frase);
        setFraseDashboard(frase);
      }

      setFraseDashboardLoading(false);
    }

    carregarFraseDashboard();

    return () => {
      ativo = false;
    };
  }, []);

  const primeiroNome = useMemo(() => getFirstName(nome), [nome]);
  const planoDisplay = useMemo(() => getPlanoDisplay(), []);

  const mostrarAvisoPix = useMemo(() => {
    if (!currentUserProfileLoaded || !currentUserProfile) {
      return false;
    }

    return (
      isPremiumUser(currentUserProfile) &&
      !hasConfiguredPix(currentUserProfile)
    );
  }, [currentUserProfile, currentUserProfileLoaded]);

  /*
   * Recalculada sempre que o dashboard é renderizado/carregado.
   * Usa o mesmo fuso enviado ao backend.
   */
  const saudacao = getGreeting("America/Sao_Paulo");

  const nfInt = useMemo(() => new Intl.NumberFormat("pt-BR"), []);

  const nfMoney = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
    []
  );

  const load = useCallback(
    async (selectedRange = range) => {
      setLoading(true);
      setErr("");

      try {
        const id = readAuth(AUTH_KEYS.USER_ID);
        const token = readAuth(AUTH_KEYS.TOKEN);

        if (!id || !token) {
          throw new Error("Token/ID ausente.");
        }

        const result = await fetchDashboard({
          token,
          id,
          range: selectedRange,
          tz: "America/Sao_Paulo",
        });

        setDash(result || null);
      } catch (error) {
        console.error("Erro ao carregar dashboard:", error);
        setErr(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [range]
  );

  useEffect(() => {
    load(range);
  }, [load, range]);

  const meta = useMemo(() => dash?.meta || null, [dash]);

  const kpis = useMemo(() => {
    return {
      totalAppointments: dash?.kpis?.totalAppointments ?? dash?.totalAppointments ?? 0,
      todayAppointments: dash?.kpis?.todayAppointments ?? dash?.today?.total ?? 0,
      totalSessions: dash?.kpis?.totalSessions ?? dash?.totalSessions ?? 0,
      completedSessions: dash?.kpis?.completedSessions ?? dash?.kpis?.totalSessions ?? 0,
      canceledAppointments:
        dash?.kpis?.canceledAppointments ?? dash?.canceledAppointments ?? 0,
      totalRevenue: dash?.kpis?.totalRevenue ?? dash?.totalRevenue ?? 0,
      pendingRevenue: dash?.kpis?.pendingRevenue ?? dash?.financial?.pendingRevenue ?? 0,
      completionRate: dash?.kpis?.completionRate ?? dash?.completionRate ?? 0,
      cancelRate: dash?.kpis?.cancelRate ?? dash?.cancelRate ?? 0,
      avgRevenuePerSession:
        dash?.kpis?.avgRevenuePerSession ?? dash?.financial?.avgRevenuePerSession ?? 0,
      sessionsWithoutPayment:
        dash?.kpis?.sessionsWithoutPayment ??
        dash?.operational?.sessionsWithoutPayment ??
        0,
      openLeads: dash?.kpis?.openLeads ?? dash?.leads?.open ?? 0,
      leadConversionRate:
        dash?.kpis?.leadConversionRate ?? dash?.leads?.conversionRate ?? 0,
      clientsWithFiscalIssues:
        dash?.kpis?.clientsWithFiscalIssues ??
        dash?.operational?.clientsWithFiscalIssues ??
        0,
    };
  }, [dash]);

  const dailyEvolution = useMemo(() => {
    return normalizeArray(getDailyEvolutionSource(dash));
  }, [dash]);

  const revenueDaily = useMemo(() => {
    return normalizeArray(getRevenueDailySource(dash));
  }, [dash]);

  const typeDistribution = useMemo(() => {
    return normalizeArray(getTypeDistributionSource(dash));
  }, [dash]);

  const byWeekday = useMemo(() => {
    return normalizeArray(getWeekdaySource(dash), EMPTY_WEEKDAY);
  }, [dash]);

  const statusData = useMemo(() => getStatusData(dash), [dash]);
  const leadOriginData = useMemo(() => getLeadOriginData(dash), [dash]);

  const lineData = useMemo(() => {
    const padded = [...dailyEvolution];

    while (padded.length < 7) {
      padded.push({ date: "", total: 0 });
    }

    return padded.slice(0, range === "30d" ? 30 : 7).map((item, index) => {
      const total = getNumber(item?.total);

      let label = DAYS[index % 7];

      if (item?.date) {
        if (range === "30d") {
          label = formatShortDateBR(item.date);
        } else {
          const date = new Date(`${item.date}T00:00:00`);

          if (!Number.isNaN(date.getTime())) {
            const dayIndex = (date.getDay() + 6) % 7;
            label = DAYS[dayIndex];
          }
        }
      }

      const revenue =
        revenueDaily.find((rev) => rev.date === item.date)?.total || 0;

      return {
        day: label,
        date: item?.date || "",
        total,
        revenue,
      };
    });
  }, [dailyEvolution, revenueDaily, range]);

  const pieData = useMemo(() => {
    return typeDistribution
      .map((item) => ({
        name: String(item?.tipo ?? item?.name ?? "Tipo"),
        value: getNumber(item?.quantidade ?? item?.value),
      }))
      .filter((item) => item.value > 0);
  }, [typeDistribution]);

  const barData = useMemo(() => {
    const values = byWeekday.map((value) => getNumber(value));
    const padded = [...values];

    while (padded.length < 7) {
      padded.push(0);
    }

    return padded.slice(0, 7).map((value, index) => ({
      day: DAYS[index],
      total: value,
    }));
  }, [byWeekday]);

  const todayAppointments = useMemo(() => {
    return normalizeArray(dash?.today?.appointments);
  }, [dash]);

  const activeTodayAppointments = useMemo(() => {
    return todayAppointments.filter(isAppointmentActiveToday);
  }, [todayAppointments]);

  const todaySummary = useMemo(() => {
    return {
      total: getNumber(kpis.todayAppointments),
      confirmed: getNumber(dash?.today?.confirmed),
      pending: getNumber(dash?.today?.pending),
      waiting: getNumber(
        dash?.today?.waitingConfirmation ??
          dash?.today?.waiting ??
          dash?.today?.aguardando_confirmacao
      ),
      completed: getNumber(dash?.today?.completed ?? dash?.today?.baixado),
      canceled: getNumber(dash?.today?.canceled ?? dash?.today?.cancelado),
      active: activeTodayAppointments.length,
    };
  }, [activeTodayAppointments.length, dash, kpis.todayAppointments]);



  const nextAppt = useMemo(() => dash?.nextAppointment || null, [dash]);

  const nextClientId = useMemo(() => {
    return (
      nextAppt?.cliente?.id ||
      nextAppt?.clienteId?._id ||
      nextAppt?.clienteId ||
      ""
    );
  }, [nextAppt]);

  const nextName = getAppointmentClientName(nextAppt);
  const nextDate = nextAppt?.dataHora ? formatDateBR(nextAppt.dataHora) : "";
  const nextTime = nextAppt?.dataHora ? formatTimeBR(nextAppt.dataHora) : "--:--";

  const dailyPriorities = useMemo(() => {
    const items = [];

    if (getNumber(todaySummary.waiting) > 0) {
      items.push({
        key: "confirmations",
        tone: "warning",
        title: `Confirmar ${getNumber(todaySummary.waiting)} consulta(s)`,
        description: "Há atendimentos aguardando confirmação para hoje.",
        route: APP_ROUTES.AGENDA,
        actionLabel: "Confirmar agora",
        score: 100,
      });
    }

    if (getNumber(kpis.sessionsWithoutPayment) > 0) {
      items.push({
        key: "payments",
        tone: "danger",
        title: `Resolver ${getNumber(kpis.sessionsWithoutPayment)} pagamento(s) pendente(s)`,
        description: "Sessões concluídas ainda aguardam registro de pagamento.",
        route: "/cobranca",
        actionLabel: "Ver pagamentos",
        score: 90,
      });
    }

    if (getNumber(kpis.openLeads) > 0) {
      items.push({
        key: "leads",
        tone: "violet",
        title: `Acompanhar ${getNumber(kpis.openLeads)} lead(s) em aberto`,
        description: "Contatos sem acompanhamento podem esfriar e perder prioridade.",
        route: APP_ROUTES.LEADS,
        actionLabel: "Abrir leads",
        score: 70,
      });
    }

    if (getNumber(kpis.clientsWithFiscalIssues) > 0) {
      items.push({
        key: "fiscal",
        tone: "warning",
        title: `Revisar ${getNumber(kpis.clientsWithFiscalIssues)} cadastro(s)`,
        description: "Existem pacientes com dados necessários ainda incompletos.",
        route: APP_ROUTES.CLIENTES,
        actionLabel: "Revisar cadastros",
        score: 60,
      });
    }

    if (getNumber(todaySummary.total) > 0) {
      items.push({
        key: "agenda",
        tone: "blue",
        title: `Conduzir ${getNumber(todaySummary.total)} atendimento(s) de hoje`,
        description: nextAppt
          ? `O próximo atendimento é com ${nextName}, às ${nextTime}.`
          : "Sua agenda já está organizada para hoje.",
        route: APP_ROUTES.AGENDA,
        actionLabel: "Abrir agenda",
        score: 50,
      });
    }

    if (!items.length) {
      items.push({
        key: "all-good",
        tone: "green",
        title: "Sua rotina está em ordem",
        description: "Nenhuma ação operacional crítica foi identificada agora.",
        route: APP_ROUTES.AGENDA,
        actionLabel: "Ver agenda",
        score: 0,
      });
    }

    return items.sort((a, b) => b.score - a.score).slice(0, 5);
  }, [
    kpis.clientsWithFiscalIssues,
    kpis.openLeads,
    kpis.sessionsWithoutPayment,
    nextAppt,
    nextName,
    nextTime,
    todaySummary.total,
    todaySummary.waiting,
  ]);

  const primaryPriority = dailyPriorities[0];
  const pendingActionsCount = dailyPriorities.filter((item) => item.key !== "all-good" && item.key !== "agenda").length;
  const operationalStatus = pendingActionsCount > 0 ? "Atenção" : "Em ordem";

  const updatedLabel = useMemo(() => {
    return meta?.generatedAt ? formatLastUpdated(meta.generatedAt) : "";
  }, [meta]);

  const canUseDashboardAi = useMemo(() => {
    const recursos = getStoredRecursos();
    return recursos?.ia === true || recursos?.insightsIa === true || canUseAnyPremiumResource();
  }, []);

  const generateDashboardInsight = useCallback(async () => {
    if (!canUseDashboardAi || !dash || aiLoading) return;

    setAiLoading(true);
    setAiError("");

    try {
      const result = await fetchDashboardInsight({
        token: readAuth(AUTH_KEYS.TOKEN),
        dashboard: dash,
      });
      setAiInsight(result?.insight || "Resumo inteligente não gerado.");
    } catch (error) {
      setAiError(getErrorMessage(error));
    } finally {
      setAiLoading(false);
    }
  }, [aiLoading, canUseDashboardAi, dash]);

  const moveCard = useCallback((targetId) => {
    if (!draggedCard || draggedCard === targetId) return;

    setCardOrder((current) => {
      const next = [...current];
      const from = next.indexOf(draggedCard);
      const to = next.indexOf(targetId);
      if (from < 0 || to < 0) return current;
      next.splice(from, 1);
      next.splice(to, 0, draggedCard);
      localStorage.setItem(DASH_LAYOUT_KEY, JSON.stringify(next));
      return next;
    });
  }, [draggedCard]);

  const moveSection = useCallback((targetId) => {
    if (!draggedSection || draggedSection === targetId) return;

    setSectionOrder((current) => {
      const next = [...current];
      const from = next.indexOf(draggedSection);
      const to = next.indexOf(targetId);
      if (from < 0 || to < 0) return current;
      next.splice(from, 1);
      next.splice(to, 0, draggedSection);
      localStorage.setItem(DASH_SECTION_LAYOUT_KEY, JSON.stringify(next));
      return next;
    });
  }, [draggedSection]);

  const moveSectionByOffset = useCallback((sectionId, offset) => {
    setSectionOrder((current) => {
      const from = current.indexOf(sectionId);
      const to = from + offset;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      localStorage.setItem(DASH_SECTION_LAYOUT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const getSectionProps = useCallback((sectionId) => ({
    style: { order: sectionOrder.indexOf(sectionId) },
    draggable: !customizingLayout,
    onDragStart: (event) => {
      setDraggedSection(sectionId);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", sectionId);
    },
    onDragOver: (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    onDrop: (event) => {
      event.preventDefault();
      moveSection(sectionId);
    },
    onDragEnd: () => setDraggedSection(""),
    "data-section-id": sectionId,
  }), [customizingLayout, moveSection, sectionOrder]);

  const renderSectionHandle = useCallback((sectionId) => (
    <div className="dash-section-handle" aria-hidden="true">
      <GripVertical size={15} />
      <small>{SECTION_LABELS[sectionId]}</small>
    </div>
  ), []);

  const resetLayout = useCallback(() => {
    setCardOrder(DEFAULT_CARD_ORDER);
    setSectionOrder(DEFAULT_SECTION_ORDER);
    localStorage.removeItem(DASH_LAYOUT_KEY);
    localStorage.removeItem(DASH_SECTION_LAYOUT_KEY);
  }, []);

  const periodLabel = useMemo(() => {
    if (range === "30d") return "Últimos 30 dias";
    if (range === "7d") return "Últimos 7 dias";

    return meta?.periodLabel || "Período selecionado";
  }, [meta, range]);

  const isFirstAccess =
    !loading &&
    !err &&
    dash &&
    getNumber(kpis.totalAppointments) === 0 &&
    getNumber(kpis.totalSessions) === 0 &&
    getNumber(kpis.totalRevenue) === 0 &&
    !nextAppt;

  return (
    <div className="dash-shell dash-shell--dark">
      <div className="dash-orbs" aria-hidden="true">
        <span className="dash-orb dash-orb--a" />
        <span className="dash-orb dash-orb--b" />
        <span className="dash-orb dash-orb--c" />
        <span className="dash-orb dash-orb--d" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="dash-topbar dash-topbar--dark">
        <button
          className="dash-btn-ghost dash-iconbtn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="dash-brand">
          <img src={Logo} alt="PsyAssist" className="dash-brand__logo" />

          <div className="dash-brand__txt">
            <div className="dash-brand__title">PsyAssist Pro</div>
            <div className="dash-brand__sub">Central do consultório</div>
          </div>
        </div>

        <div className="dash-topbar__actions">
          <button
            className="dash-btn-ghost dash-topbar__new"
            type="button"
            onClick={() => navigate(APP_ROUTES.AGENDAMENTO_NOVO)}
          >
            <CalendarPlus size={17} aria-hidden="true" />
            Novo agendamento
          </button>

          <button
            className="dash-btn-ghost"
            type="button"
            onClick={() => load(range)}
            disabled={loading}
          >
            <RefreshCw
              size={16}
              aria-hidden="true"
              className={loading ? "dash-icon-spin" : ""}
            />
            {loading ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="dash-main dash-main--dark">
        {loading ? (
          <DashboardState
            type="loading"
            title="Carregando sua central..."
            detail="Consolidando agenda, financeiro, leads e pendências operacionais."
          />
        ) : err ? (
          <DashboardState
            type="error"
            title="Não foi possível carregar o dashboard."
            detail={err}
            actionLabel="Tentar novamente"
            onAction={() => load(range)}
          />
        ) : !dash ? (
          <DashboardState
            type="empty"
            title="Sem dados no momento."
            detail="Assim que houver agendamentos, sessões, leads ou pagamentos, seus indicadores aparecerão aqui."
            actionLabel="Atualizar"
            onAction={() => load(range)}
          />
        ) : (
          <div className="dash-exec">
            <section className="dash-layout-toolbar dash-glass">
              <div>
                <strong>Personalize seu dashboard</strong>
                <span>Arraste os blocos no computador ou use os controles no celular.</span>
              </div>
              <div className="dash-layout-toolbar__actions">
                <button
                  type="button"
                  className="dash-btn dash-btn--ghost2"
                  onClick={() =>
                    setCustomizingLayout(
                      (current) => !current
                    )
                  }
                >
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  {customizingLayout ? "Concluir" : "Personalizar"}
                </button>
                <button
                  type="button"
                  className="dash-btn dash-btn--ghost2"
                  onClick={resetLayout}
                >
                  <RotateCcw size={16} aria-hidden="true" />
                  Restaurar padrão
                </button>
              </div>
            </section>

            {customizingLayout ? (
              <section className="dash-layout-editor dash-glass">
                <div className="dash-layout-editor__head">
                  <strong>Ordem dos blocos</strong>
                  <span>Use as setas para ajustar a ordem com precisão.</span>
                </div>
                <div className="dash-layout-editor__list">
                  {sectionOrder.map((sectionId, index) => (
                    <div className="dash-layout-editor__item" key={sectionId}>
                      <span className="dash-layout-editor__index">{index + 1}</span>
                      <strong>{SECTION_LABELS[sectionId]}</strong>
                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            moveSectionByOffset(
                              sectionId,
                              -1
                            )
                          }
                          disabled={index === 0}
                          aria-label={`Mover ${SECTION_LABELS[sectionId]} para cima`}
                        >
                          <ChevronUp size={16} aria-hidden="true" />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            moveSectionByOffset(
                              sectionId,
                              1
                            )
                          }
                          disabled={
                            index ===
                            sectionOrder.length - 1
                          }
                          aria-label={`Mover ${SECTION_LABELS[sectionId]} para baixo`}
                        >
                          <ChevronDown size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {mostrarAvisoPix ? (
              <section
                className="dash-pix-setup dash-glass"
                aria-label="Configuração de cobrança via Pix"
              >
                <div className="dash-pix-setup__icon" aria-hidden="true">
                  <WalletCards size={22} />
                </div>

                <div className="dash-pix-setup__content">
                  <div className="dash-pix-setup__title-row">
                    <strong>Configure sua chave Pix</strong>
                    <span>Premium</span>
                  </div>

                  <p>
                    Cadastre sua chave Pix para que ela possa ser incluída
                    automaticamente nas comunicações de cobrança enviadas aos
                    seus clientes.
                  </p>
                </div>

                <button
                  type="button"
                  className="dash-pix-setup__action"
                  onClick={() => navigate(APP_ROUTES.PERFIL)}
                >
                  Cadastrar chave Pix
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </section>
            ) : null}

            <section className={`dash-hero dash-custom-section ${draggedSection === "hero" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("hero")}>
              {renderSectionHandle("hero")}
              <div className="dash-hero__left">
                <span className="dash-eyebrow">
                  {isFirstAccess ? "Primeiro acesso" : "Sua rotina de hoje"}
                </span>

                <div className="dash-hero__identity">
                  <div className="dash-hero__title">
                    {isFirstAccess
                      ? `Bem-vindo${primeiroNome ? `, ${primeiroNome}` : ""}`
                      : `${saudacao}${primeiroNome ? `, ${primeiroNome}` : ""}.`}
                  </div>

                  {currentUserProfile?.membroFundador?.ativo === true ? (
                    <FounderBadge
                      concedidoEm={
                        currentUserProfile.membroFundador.concedidoEm
                      }
                      origem={
                        currentUserProfile.membroFundador.origem
                      }
                    />
                  ) : null}
                </div>

                <div className="dash-hero__sub">
                  {isFirstAccess
                    ? "Configure a base do consultório para transformar sua rotina em indicadores claros, agenda previsível e gestão financeira profissional."
                    : primaryPriority?.description ||
                      "Seu panorama operacional está atualizado e pronto para orientar as próximas ações."}
                  {updatedLabel ? ` • Atualizado: ${updatedLabel}` : ""}
                </div>
                {(fraseDashboardLoading || fraseDashboard?.texto) ? (
                  <div
                    className={`dash-thought-mini ${
                      fraseDashboardLoading
                        ? "dash-thought-mini--loading"
                        : "dash-thought-mini--ready"
                    }`}
                    aria-label="Pensamento do dia"
                  >
                    {fraseDashboardLoading ? (
                      <span className="dash-thought-mini__loading">
                        Carregando pensamento do dia...
                      </span>
                    ) : (
                      <>
                        <Sparkles
                          size={13}
                          className="dash-thought-mini__icon"
                          aria-hidden="true"
                        />

                        <span className="dash-thought-mini__quote">
                          “{fraseDashboard.texto}”
                        </span>

                        {fraseDashboard.autor ? (
                          <span className="dash-thought-mini__author">
                            — {fraseDashboard.autor}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}

<div className="dash-hero__actions">
                  <button
                    type="button"
                    className="dash-btn dash-btn--primary"
                    onClick={() => navigate(APP_ROUTES.AGENDAMENTO_NOVO)}
                  >
                    <CalendarPlus size={17} aria-hidden="true" />
                    Criar agendamento
                  </button>

                  <button
                    type="button"
                    className="dash-btn dash-btn--ghost2"
                    onClick={() => navigate(APP_ROUTES.LEAD_NOVO)}
                  >
                    Novo lead
                  </button>

                  <button
                    type="button"
                    className="dash-btn dash-btn--ghost2"
                    onClick={() => navigate(APP_ROUTES.FINANCEIRO)}
                  >
                    Ver financeiro
                  </button>
                </div>
              </div>

              <div className="dash-hero__right">
                <span className="dash-chip">
                  <span className="dash-chip__dot" />
                  {periodLabel}
                </span>

                <div className="dash-hero-score dash-glass">
                  <span>Status do dia</span>
                  <strong>{operationalStatus}</strong>
                  <small>
                    {pendingActionsCount > 0
                      ? `${pendingActionsCount} prioridade(s) operacional(is) precisam da sua atenção.`
                      : "Nenhuma pendência crítica detectada. Sua rotina está organizada."}
                  </small>
                </div>
              </div>
            </section>

            {isFirstAccess ? (
              <section className={`dash-onboarding dash-custom-section ${draggedSection === "onboarding" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("onboarding")}>
                {renderSectionHandle("onboarding")}
                <div className="dash-onboarding__main dash-glass">
                  <div className="dash-onboarding__badge">Primeiros passos</div>

                  <h2>Comece seu consultório digital com uma base forte</h2>

                  <p>
                    Seu ambiente já está ativo. Cadastre pacientes, crie o
                    primeiro agendamento e registre recebimentos para liberar
                    uma visão completa da sua operação.
                  </p>

                  <div className="dash-onboarding__actions">
                    <button
                      type="button"
                      className="dash-btn dash-btn--primary"
                      onClick={() => navigate(APP_ROUTES.CLIENTES_NOVO)}
                    >
                      Cadastrar paciente
                    </button>

                    <button
                      type="button"
                      className="dash-btn dash-btn--ghost2"
                      onClick={() => navigate(APP_ROUTES.AGENDAMENTO_NOVO)}
                    >
                      Criar agendamento
                    </button>
                  </div>
                </div>

                <div className="dash-onboarding__steps dash-glass">
                  <OnboardingStep
                    number="1"
                    title="Cadastre pacientes"
                    desc="Organize dados clínicos e fiscais desde o início."
                    done={false}
                  />

                  <OnboardingStep
                    number="2"
                    title="Crie agendamentos"
                    desc="Sua agenda passa a alimentar os indicadores."
                    done={false}
                  />

                  <OnboardingStep
                    number="3"
                    title="Registre pagamentos"
                    desc="A visão financeira fica clara e previsível."
                    done={false}
                  />
                </div>
              </section>
            ) : null}

            <section className={`dash-filters dash-glass dash-custom-section ${draggedSection === "filters" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("filters")}>
              {renderSectionHandle("filters")}
              <div className="dash-filters__grid">
                <div className="dash-field">
                  <div className="dash-field__label">Usuário</div>
                  <div className="dash-field__value">{nome || "—"}</div>
                </div>

                <div className="dash-field dash-field--stretch">
                  <div className="dash-field__label">Período</div>

                  <div className="dash-field__value">
                    <div className="dash-seg">
                      <button
                        type="button"
                        className={`dash-seg__btn ${
                          range === "7d" ? "dash-seg__btn--active" : ""
                        }`}
                        onClick={() => setRange("7d")}
                      >
                        7 dias
                      </button>

                      <button
                        type="button"
                        className={`dash-seg__btn ${
                          range === "30d" ? "dash-seg__btn--active" : ""
                        }`}
                        onClick={() => setRange("30d")}
                      >
                        30 dias
                      </button>
                    </div>
                  </div>
                </div>

                <div className="dash-field dash-field--right">
                  <div className="dash-field__label">Modo</div>
                  <div className="dash-field__value">{planoDisplay}</div>
                </div>
              </div>

              <div className="dash-filters__actions">
                <button
                  className="dash-btn dash-btn--ghost2"
                  type="button"
                  onClick={() => load(range)}
                  disabled={loading}
                >
                  Aplicar período
                </button>

                <button
                  className="dash-btn dash-btn--ghost2"
                  type="button"
                  onClick={() => navigate(APP_ROUTES.RELATORIOS)}
                >
                  Abrir relatórios
                </button>
              </div>
            </section>

            <section
              className={`dash-google-calendar ${
                googleCalendarStatus.conectado
                  ? "dash-google-calendar--connected"
                  : "dash-google-calendar--disconnected"
              }`}
            >
              <div className="dash-google-calendar__logo" aria-hidden="true">
                G
              </div>

              <div className="dash-google-calendar__content">
                <strong>
                  {googleCalendarStatus.carregando
                    ? "Consultando Google Agenda..."
                    : googleCalendarStatus.conectado
                      ? "Google Agenda conectado"
                      : "Conecte seu Google Agenda"}
                </strong>

                <span>
                  {googleCalendarStatus.conectado
                    ? "Novos agendamentos e alterações são sincronizados automaticamente."
                    : "Leve seus próximos atendimentos para o Google Agenda automaticamente."}
                </span>
              </div>

              <button
                type="button"
                onClick={() => navigate("/integrations")}
              >
                {googleCalendarStatus.conectado
                  ? "Gerenciar"
                  : "Conectar"}
              </button>
            </section>

            <section className={`dash-agenda-board dash-glass dash-custom-section ${draggedSection === "today" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("today")}>
              {renderSectionHandle("today")}
              <div className="dash-agenda-board__head">
                <div>
                  <span className="dash-section-kicker">Central operacional</span>
                  <h2>Sua rotina de hoje</h2>
                  <p>
                    Veja o que precisa ser feito agora, acompanhe a agenda e encerre o dia sem pendências esquecidas.
                  </p>
                </div>

                <div className="dash-agenda-board__summary">
                  <strong>{nfInt.format(todaySummary.total)}</strong>
                  <span>atendimento(s) hoje</span>
                </div>
              </div>

              <div className="dash-agenda-layout">
                <div className="dash-next-card dash-next-card--hero">
                  {!nextAppt ? (
                    <EmptyAction
                      icon={<CalendarDays size={24} aria-hidden="true" />}
                      title="Nenhuma consulta agendada"
                      desc="Crie um agendamento para organizar sua rotina clínica e alimentar o dashboard."
                      actionLabel="Agendar consulta"
                      onAction={() => navigate(APP_ROUTES.AGENDAMENTO_NOVO)}
                    />
                  ) : (
                    <>
                      <div className="dash-next-card__time">
                        <strong>{nextTime}</strong>
                        <span>{nextDate}</span>
                      </div>

                      <div className="dash-next-card__body">
                        <span>Próximo atendimento</span>
                        <strong>{nextName}</strong>
                        <small>{getAppointmentService(nextAppt)}</small>

                        <div className="dash-next-card__meta">
                          <em className={`dash-status-pill dash-status-pill--${getAppointmentStatusTone(nextAppt?.status)}`}>
                            {getAppointmentStatusLabel(nextAppt?.status)}
                          </em>

                          {wasConfirmedByClient(nextAppt) ? (
                            <em
                              className="dash-client-confirmation-badge"
                              title="Presença confirmada pelo próprio cliente através do link enviado."
                            >
                              <span aria-hidden="true">
                                <Check size={12} />
                              </span>
                              {getClientConfirmationLabel(nextAppt)}
                            </em>
                          ) : null}

                          {nextAppt?.valorAgendamento ? (
                            <em className="dash-next-card__value">
                              {nfMoney.format(getNumber(nextAppt.valorAgendamento))}
                            </em>
                          ) : null}
                        </div>
                      </div>

                      <div className="dash-next-card__actions">
                        <button
                          type="button"
                          className="dash-btn dash-btn--primary"
                          onClick={() => navigate(APP_ROUTES.AGENDA)}
                        >
                          Abrir agenda
                        </button>

                        <button
                          type="button"
                          className="dash-btn dash-btn--ghost2"
                          onClick={() =>
                            navigate(APP_ROUTES.SESSAO_NOVA, {
                              state: {
                                agendamentoId: nextAppt?.id || nextAppt?._id || "",
                                clienteId: nextClientId,
                                nome: nextName,
                                nomeCliente: nextName,
                                appointmentData: nextAppt,
                              },
                            })
                          }
                        >
                          Lançar sessão
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="dash-today-panel">
                  <div className="dash-today-panel__head">
                    <div>
                      <strong>Timeline do dia</strong>
                      <span>{todaySummary.active} atendimento(s) em aberto</span>
                    </div>

                    <button
                      type="button"
                      className="dash-btn dash-btn--ghost2 dash-today-panel__btn"
                      onClick={() => navigate(APP_ROUTES.AGENDAMENTO_NOVO)}
                    >
                      Novo
                    </button>
                  </div>

                  <div className="dash-today-stats">
                    <span><b>{nfInt.format(todaySummary.confirmed)}</b> confirmados</span>
                    <span><b>{nfInt.format(todaySummary.pending)}</b> pendentes</span>
                    <span><b>{nfInt.format(todaySummary.waiting)}</b> aguardando</span>
                  </div>

                  <div className="dash-today-list">
                    {todayAppointments.length ? (
                      todayAppointments.slice(0, 6).map((appointment) => (
                        <button
                          type="button"
                          className={`dash-today-row dash-today-row--${getAppointmentStatusTone(appointment.status)}`}
                          key={appointment.id}
                          onClick={() => navigate(APP_ROUTES.AGENDA)}
                        >
                          <span className="dash-today-row__time">
                            {formatTimeBR(appointment.dataHora)}
                          </span>

                          <span className="dash-today-row__main">
                            <strong>{getAppointmentClientName(appointment)}</strong>

                            <span className="dash-today-row__details">
                              <small>{getAppointmentService(appointment)}</small>

                              {appointment?.googleCalendar?.sincronizado &&
                              appointment?.googleCalendar?.eventId ? (
                                <span
                                  className="dash-google-event-badge"
                                  title="Evento sincronizado com o Google Agenda."
                                >
                                  <b aria-hidden="true">G</b>
                                  Google
                                </span>
                              ) : null}


                              {wasConfirmedByClient(appointment) ? (
                                <span
                                  className="dash-client-confirmation-badge dash-client-confirmation-badge--compact"
                                  title="Presença confirmada pelo próprio cliente."
                                >
                                  <span aria-hidden="true">
                                <Check size={12} />
                              </span>
                                  Cliente confirmou
                                </span>
                              ) : null}
                            </span>
                          </span>

                          <span className="dash-today-row__status">
                            {getAppointmentStatusLabel(appointment.status)}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="dash-today-empty dash-today-empty--board">
                        Nenhum atendimento para hoje. Aproveite para revisar pendências ou captar novos leads.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className={`dash-command-grid dash-command-grid--compact dash-custom-section ${draggedSection === "commands" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("commands")}>
              {renderSectionHandle("commands")}
              {cardOrder.map((cardId) => {
                const cards = {
                  agenda: { tone: "blue", label: "Resumo da agenda", value: nfInt.format(getNumber(kpis.todayAppointments)), desc: `${getNumber(dash?.today?.confirmed)} confirmado(s), ${getNumber(dash?.today?.pending)} pendente(s)`, action: "Ver agenda", onClick: () => navigate(APP_ROUTES.AGENDA) },
                  receita: { tone: "green", label: "Receita recebida", value: nfMoney.format(getNumber(kpis.totalRevenue)), desc: `Ticket médio: ${nfMoney.format(getNumber(kpis.avgRevenuePerSession))}`, action: "Financeiro", onClick: () => navigate(APP_ROUTES.FINANCEIRO) },
                  pendente: { tone: getNumber(kpis.pendingRevenue) > 0 ? "warning" : "green", label: "Receita pendente", value: nfMoney.format(getNumber(kpis.pendingRevenue)), desc: "Valores em aberto no período", action: "Conferir", onClick: () => navigate(APP_ROUTES.FINANCEIRO) },
                  leads: { tone: getNumber(kpis.openLeads) > 0 ? "violet" : "green", label: "Leads abertos", value: nfInt.format(getNumber(kpis.openLeads)), desc: `${getNumber(kpis.leadConversionRate)}% de conversão no período`, action: "Ver leads", onClick: () => navigate(APP_ROUTES.LEADS) },
                };
                const card = cards[cardId];
                return (
                  <div
                    key={cardId}
                    className={`dash-draggable ${draggedCard === cardId ? "dash-draggable--active" : ""}`}
                    draggable
                    onDragStart={() => setDraggedCard(cardId)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => moveCard(cardId)}
                    onDragEnd={() => setDraggedCard("")}
                  >
                    <span
                      className="dash-drag-handle"
                      title="Arrastar card"
                      aria-hidden="true"
                    >
                      <GripVertical size={16} />
                    </span>
                    <CommandCard {...card} />
                  </div>
                );
              })}
            </section>

            <section className={`dash-kpis dash-kpis--compact dash-custom-section ${draggedSection === "kpis" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("kpis")}>
              {renderSectionHandle("kpis")}
              <KpiCard
                variant="blue"
                label="Agendamentos"
                value={nfInt.format(getNumber(kpis.totalAppointments))}
                hint="Total no período"
              />

              <KpiCard
                variant="green"
                label="Sessões"
                value={nfInt.format(getNumber(kpis.completedSessions))}
                hint="Sessões concluídas"
              />

              <KpiCard
                variant={getNumber(kpis.cancelRate) > 15 ? "warn" : "blue"}
                label="Conclusão"
                value={`${nfInt.format(getNumber(kpis.completionRate))}%`}
                hint={`${nfInt.format(getNumber(kpis.cancelRate))}% de cancelamento`}
              />

              <KpiCard
                variant={getNumber(kpis.sessionsWithoutPayment) > 0 ? "warn" : "green"}
                label="Sem cobrança"
                value={nfInt.format(getNumber(kpis.sessionsWithoutPayment))}
                hint="Concluídas sem receita"
              />
            </section>

            <section className={`dash-ai-master dash-glass dash-custom-section ${canUseDashboardAi ? "" : "dash-ai-master--locked"} ${draggedSection === "agent-summary" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("agent-summary")}>
              {renderSectionHandle("agent-summary")}
              <div className="dash-ai-master__head">
                <div>
                  <span className="dash-section-kicker">Agente PsyAssist</span>
                  <h2>Plano inteligente do dia</h2>
                  <p>Transforma agenda, financeiro, leads e pendências em uma orientação objetiva para o seu próximo passo.</p>
                </div>
                <button
                  type="button"
                  className="dash-btn dash-btn--primary"
                  onClick={generateDashboardInsight}
                  disabled={!canUseDashboardAi || aiLoading}
                >
                  {aiLoading ? "Analisando..." : canUseDashboardAi ? "Gerar plano do dia" : "Disponível no Premium"}
                </button>
              </div>

              {aiError ? <div className="dash-ai-master__error">{aiError}</div> : null}
              {aiInsight ? <div className="dash-ai-master__result">{aiInsight}</div> : (
                <div className="dash-ai-master__empty">
                  {canUseDashboardAi
                    ? "Gere uma orientação prática com prioridades, riscos e a melhor próxima ação para hoje."
                    : "No Premium, a IA transforma os indicadores do consultório em uma lista objetiva de ações."}
                </div>
              )}
            </section>

            <section className={`dash-focus-grid dash-focus-grid--single dash-custom-section ${draggedSection === "attention" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("attention")}>
              {renderSectionHandle("attention")}
              <div className="dash-focus-panel dash-glass dash-routine-panel">
                <div className="dash-routine-panel__head">
                  <div>
                    <span className="dash-section-kicker">Próximas ações</span>
                    <h2>O que você precisa fazer agora</h2>
                    <p>As prioridades abaixo são montadas com os dados atuais da sua operação.</p>
                  </div>
                  <div className={`dash-routine-status dash-routine-status--${pendingActionsCount > 0 ? "attention" : "good"}`}>
                    <strong>{pendingActionsCount}</strong>
                    <span>{pendingActionsCount === 1 ? "prioridade" : "prioridades"}</span>
                  </div>
                </div>

                <div className="dash-routine-list">
                  {dailyPriorities.map((item, index) => (
                    <button
                      key={item.key}
                      type="button"
                      className={`dash-routine-item dash-routine-item--${item.tone || "blue"}`}
                      onClick={() => item.route && navigate(item.route)}
                    >
                      <span className="dash-routine-item__number">{index + 1}</span>
                      <span className="dash-routine-item__content">
                        <strong>{item.title}</strong>
                        <small>{item.description}</small>
                      </span>
                      <span className="dash-routine-item__action">
                        {item.actionLabel}
                        <ArrowRight size={14} aria-hidden="true" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className={`dash-panels dash-custom-section ${draggedSection === "analytics" ? "dash-custom-section--dragging" : ""}`} {...getSectionProps("analytics")}>
              {renderSectionHandle("analytics")}
              <div className="dash-panel dash-span-8">
                <PanelHeader
                  title="Agenda e receita por dia"
                  desc="Compare volume de agendamentos com receita recebida no período."
                />

                <div className="dash-panel__body">
                  <ChartFrame height={330}>
                    {(chartWidth, chartHeight) => (
                    <AreaChart width={chartWidth} height={chartHeight} data={lineData}>
                      <defs>
                        <linearGradient id="dashBlue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.42} />
                          <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="dashGreen" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.34} />
                          <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis yAxisId="left" allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" hide />
                      <Tooltip content={<CustomTooltip moneyFormatter={nfMoney} />} />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="total"
                        name="Agendamentos"
                        stroke={COLORS.blue}
                        fill="url(#dashBlue)"
                        strokeWidth={3}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        name="Receita"
                        stroke={COLORS.green}
                        strokeWidth={3}
                        dot={false}
                      />
                    </AreaChart>
                    )}
                  </ChartFrame>
                </div>
              </div>

              <div className="dash-panel dash-span-4">
                <PanelHeader
                  title="Status da agenda"
                  desc="Distribuição operacional dos agendamentos."
                />

                <div className="dash-panel__body">
                  {statusData.length === 0 ? (
                    <EmptyAction
                      icon={<BarChart3 size={24} aria-hidden="true" />}
                      title="Sem status no período"
                      desc="A distribuição aparecerá após criar agendamentos."
                      actionLabel="Criar agendamento"
                      onAction={() => navigate(APP_ROUTES.AGENDAMENTO_NOVO)}
                    />
                  ) : (
                    <ChartFrame height={310}>
                      {(chartWidth, chartHeight) => (
                      <PieChart width={chartWidth} height={chartHeight}>
                        <Pie
                          data={statusData}
                          dataKey="total"
                          nameKey="label"
                          innerRadius={62}
                          outerRadius={96}
                          paddingAngle={3}
                        >
                          {statusData.map((item, index) => (
                            <Cell
                              key={`status-${item.status}-${index}`}
                              fill={STATUS_COLORS[item.status] || COLORS.blue}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                      )}
                    </ChartFrame>
                  )}
                </div>
              </div>

              <div className="dash-panel dash-span-4">
                <PanelHeader
                  title="Tipos de consulta"
                  desc="Proporção por serviço."
                />

                <div className="dash-panel__body">
                  {pieData.length === 0 ? (
                    <EmptyAction
                      icon={<TrendingUp size={24} aria-hidden="true" />}
                      title="Sem distribuição"
                      desc="Após criar agendamentos, esta área mostrará os tipos de consulta."
                      actionLabel="Configurar serviços"
                      onAction={() => navigate(APP_ROUTES.SERVICOS)}
                    />
                  ) : (
                    <ChartFrame height={290}>
                      {(chartWidth, chartHeight) => (
                      <PieChart width={chartWidth} height={chartHeight}>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          outerRadius={94}
                          label={({ percent }) => `${Math.round(percent * 100)}%`}
                        >
                          {pieData.map((_, index) => (
                            <Cell
                              key={`pie-cell-${index}`}
                              fill={[COLORS.green, COLORS.blue, COLORS.violet][index % 3]}
                            />
                          ))}
                        </Pie>

                        <Tooltip />
                        <Legend />
                      </PieChart>
                      )}
                    </ChartFrame>
                  )}
                </div>
              </div>

              <div className="dash-panel dash-span-4">
                <PanelHeader
                  title="Leads por origem"
                  desc="Canais que geraram contatos."
                />

                <div className="dash-panel__body">
                  {leadOriginData.length === 0 ? (
                    <EmptyAction
                      icon={<Sparkles size={24} aria-hidden="true" />}
                      title="Sem leads no período"
                      desc="Registre leads para medir canais de captação."
                      actionLabel="Novo lead"
                      onAction={() => navigate(APP_ROUTES.LEAD_NOVO)}
                    />
                  ) : (
                    <ChartFrame height={290}>
                      {(chartWidth, chartHeight) => (
                      <BarChart width={chartWidth} height={chartHeight} data={leadOriginData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="origem" width={90} />
                        <Tooltip />
                        <Bar dataKey="total" fill={COLORS.violet} radius={[0, 8, 8, 0]} />
                      </BarChart>
                      )}
                    </ChartFrame>
                  )}
                </div>
              </div>

              <div className="dash-panel dash-span-4">
                <PanelHeader
                  title="Consultas por dia"
                  desc="Volume por dia da semana."
                />

                <div className="dash-panel__body">
                  <ChartFrame height={290}>
                    {(chartWidth, chartHeight) => (
                    <BarChart width={chartWidth} height={chartHeight} data={barData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar
                        dataKey="total"
                        fill={COLORS.cyan}
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                    )}
                  </ChartFrame>
                </div>
              </div>
            </section>
          </div>
        )}
         </main>

<AgentFloatingChat
  enabled={canUseDashboardAi}
  days={range === "7d" ? 7 : 30}
  timezone="America/Sao_Paulo"
/>
</div>
  );
}

function DashboardState({
  type = "empty",
  title,
  detail,
  actionLabel,
  onAction,
}) {
  return (
    <div className="dash-state dash-glass">
      {type === "loading" ? (
        <div className="dash-loader" />
      ) : (
        <div className="dash-state__icon">
          {type === "error" ? (
            <AlertTriangle size={25} aria-hidden="true" />
          ) : (
            <LayoutDashboard size={25} aria-hidden="true" />
          )}
        </div>
      )}

      <div className="dash-state__title">{title}</div>

      {detail ? <div className="dash-state__detail">{detail}</div> : null}

      {actionLabel && onAction ? (
        <button
          className="dash-btn dash-btn--primary"
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function CommandCard({ label, value, desc, action, tone = "blue", onClick }) {
  return (
    <button
      type="button"
      className={`dash-command dash-command--${tone}`}
      onClick={onClick}
    >
      <span className="dash-command__shine" />
      <span className="dash-command__label">{label}</span>
      <strong>{value}</strong>
      <small>{desc}</small>
      <em>
        {action}
        <ArrowRight size={14} aria-hidden="true" />
      </em>
    </button>
  );
}

function KpiCard({ label, value, hint, variant = "blue" }) {
  return (
    <div className={`dash-kpi dash-glass dash-kpi--${variant}`}>
      <div className="dash-kpi__top">
        <div className="dash-kpi__label">{label}</div>
      </div>

      <div className="dash-kpi__value">{value}</div>
      <div className="dash-kpi__hint">{hint}</div>
    </div>
  );
}

function PanelHeader({ title, desc }) {
  return (
    <div className="dash-panel__head">
      <div>
        <div className="dash-panel__title">{title}</div>
        <div className="dash-panel__desc">{desc}</div>
      </div>
    </div>
  );
}

function OnboardingStep({ number, title, desc, done }) {
  return (
    <div className={`dash-step ${done ? "dash-step--done" : ""}`}>
      <div className="dash-step__number">
        {done ? (
          <Check size={17} aria-hidden="true" />
        ) : (
          number
        )}
      </div>

      <div className="dash-step__content">
        <div className="dash-step__title">{title}</div>
        <div className="dash-step__desc">{desc}</div>
      </div>
    </div>
  );
}

function EmptyAction({ icon, title, desc, actionLabel, onAction }) {
  return (
    <div className="dash-empty-action">
      <div className="dash-empty-action__icon">{icon}</div>

      <div>
        <div className="dash-empty-action__title">{title}</div>
        <div className="dash-empty-action__desc">{desc}</div>
      </div>

      {actionLabel && onAction ? (
        <button
          type="button"
          className="dash-btn dash-btn--ghost2 dash-empty-action__btn"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}
